/**
 * CalendarView — month calendar.
 * Per app-codex: surface "tasks.calendar". Behaviour: tasks.byDate.
 * Per app-fragments: FreshCards revibe (mirror) of calendar-view.
 * Dev mode: uses devDb.
 */
import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useApp } from '../store'
import { useEffect } from 'react'
import { fetchTasks, type DbTask } from '../db'
import { devDb } from '../devMode'

export function CalendarView() {
  const [cursor, setCursor] = useState(new Date())
  const [tasks, setTasks] = useState<DbTask[]>([])
  const { openPeek, dev } = useApp()

  useEffect(() => {
    const load = dev
      ? devDb.tasks.list()
      : fetchTasks('family')
    load.then(({ data }) => setTasks(data ?? []))
  }, [dev])

  const year = cursor.getFullYear()
  const month = cursor.getMonth()
  const firstDay = new Date(year, month, 1)
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const startWeekday = firstDay.getDay()

  const cells: { date: Date | null; tasks: DbTask[] }[] = []
  for (let i = 0; i < startWeekday; i++) cells.push({ date: null, tasks: [] })
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d)
    const dateStr = date.toISOString().split('T')[0]
    const dayTasks = tasks.filter((t) => t.due?.split('T')[0] === dateStr)
    cells.push({ date, tasks: dayTasks })
  }

  return (
    <div>
      <header className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-skin-text">
          {cursor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
        </h1>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCursor(new Date(year, month - 1, 1))}
            className="p-2 rounded-lg hover:bg-slate-100 min-h-touch min-w-touch"
            aria-label="Previous month"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => setCursor(new Date())}
            className="px-3 py-2 rounded-lg hover:bg-slate-100 text-sm font-medium min-h-touch"
          >
            Today
          </button>
          <button
            onClick={() => setCursor(new Date(year, month + 1, 1))}
            className="p-2 rounded-lg hover:bg-slate-100 min-h-touch min-w-touch"
            aria-label="Next month"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="px-2 py-2 text-center text-xs font-medium text-slate-500">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((cell, i) => (
            <div
              key={i}
              className={`min-h-[80px] sm:min-h-[100px] border-r border-b border-slate-200 p-1 ${
                cell.date ? 'bg-white' : 'bg-slate-50'
              }`}
            >
              {cell.date && (
                <>
                  <div className="text-xs text-slate-500 font-medium">
                    {cell.date.getDate()}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {cell.tasks.slice(0, 3).map((t) => (
                      <button
                        key={t.id}
                        onClick={() => openPeek({ kind: 'task', id: t.id })}
                        className="block w-full text-left text-xs bg-garden-100 text-garden-800 rounded px-1.5 py-0.5 truncate hover:bg-garden-200"
                      >
                        {t.title}
                      </button>
                    ))}
                    {cell.tasks.length > 3 && (
                      <div className="text-xs text-slate-400">+{cell.tasks.length - 3} more</div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
